import { useState } from "react";
import { toast } from "sonner";
import { Sparkles, Loader2, Minus, Plus, MessageCircle, MapPin, Car, Lightbulb, Wallet } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { http, formatErr, waLink } from "@/lib/api";
import { useWaNumber } from "@/components/site/WhatsAppFab";
import MapView from "@/components/site/MapView";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

const INTERESTS = ["nature", "lake", "beach", "culture", "culinary", "adventure", "photo", "family"];
const BUDGETS = [2500000, 5000000, 10000000, 20000000];

const Stepper = ({ label, value, set, min, max, testid, suffix }) => (
  <div>
    <p className="mb-2 text-sm font-semibold text-slate-700">{label}</p>
    <div className="flex items-center justify-between rounded-2xl border border-slate-200 bg-white px-3 py-2">
      <button type="button" data-testid={`${testid}-minus`} onClick={() => set(Math.max(min, value - 1))} className="rounded-full border p-1.5 hover:bg-slate-100"><Minus className="h-4 w-4" /></button>
      <span data-testid={testid} className="font-bold">{value} {suffix}</span>
      <button type="button" data-testid={`${testid}-plus`} onClick={() => set(Math.min(max, value + 1))} className="rounded-full border p-1.5 hover:bg-slate-100"><Plus className="h-4 w-4" /></button>
    </div>
  </div>
);

function PlannerForm({ onResult, busy, setBusy }) {
  const { t, lang, money, fromIdr, toIdr, currency, ratesReady, ratesError, retryRates } = useLang();
  const [days, setDays] = useState(3);
  const [pax, setPax] = useState(2);
  const [budget, setBudget] = useState(5000000);
  const [interests, setInterests] = useState(["nature", "culinary"]);
  const [city, setCity] = useState("Padang");
  const toggle = (k) => setInterests(interests.includes(k) ? interests.filter((x) => x !== k) : [...interests, k]);
  const submit = async (e) => {
    e.preventDefault();
    if (!ratesReady) return;
    if (!interests.length) return toast.error(t("interests"));
    setBusy(true);
    try {
      const { data } = await http.post("/ai/itinerary", { days, participants: pax, budget: Math.max(100000, Number(budget)), interests: interests.map((k) => t(`int_${k}`)), start_city: city, lang });
      for (let i = 0; i < 90; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        const { data: doc } = await http.get(`/ai/itinerary/${data.id}`);
        if (doc.status === "done") { onResult(doc); return; }
        if (doc.status === "error") throw new Error(doc.error || "AI error");
      }
      throw new Error("Timeout");
    } catch (err) { toast.error(formatErr(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} data-testid="ai-trip-planner" className="space-y-5 rounded-3xl border border-slate-200 bg-white p-6 shadow-xl shadow-slate-200/50">
      <div className="grid grid-cols-2 gap-4">
        <Stepper label={t("duration")} value={days} set={setDays} min={1} max={10} testid="ai-planner-duration-input" suffix={t("days")} />
        <Stepper label={t("participants")} value={pax} set={setPax} min={1} max={50} testid="ai-planner-participants-input" suffix="pax" />
      </div>
      <div>
        <p className="mb-2 text-sm font-semibold text-slate-700">{t("budget")} ({currency})</p>
        <div className="grid grid-cols-4 gap-2 mb-2">
          {BUDGETS.map((b) => <button type="button" key={b} disabled={!ratesReady} data-testid={`ai-budget-preset-${b}`} onClick={() => setBudget(b)} className={`rounded-xl border px-1 py-2 text-xs font-bold transition-colors ${Number(budget) === b ? "border-[#064E3B] bg-emerald-50 text-[#064E3B]" : "border-slate-200 text-slate-600 hover:border-slate-400"}`}>{!ratesReady ? "—" : lang === "id" ? `${b / 1000000} jt` : money(b)}</button>)}
        </div>
        <input data-testid="ai-planner-budget-select" disabled={!ratesReady} type="number" min={fromIdr(100000)} step={lang === "id" ? 100000 : "any"} value={fromIdr(Number(budget))} onChange={(e) => setBudget(toIdr(Number(e.target.value)))} className="w-full rounded-2xl border border-slate-200 px-4 py-3 font-semibold outline-none focus:border-[#064E3B]" />
        {!ratesReady && <p role="status" data-testid="planner-rates-status" className="mt-2 text-sm text-slate-500">{t(ratesError ? "rates_unavailable" : "rates_loading")}</p>}
        {!ratesReady && ratesError && <button type="button" data-testid="planner-rates-retry" onClick={retryRates} className="mt-2 min-h-11 rounded-full border border-emerald-200 px-4 py-2 text-sm font-bold text-[#064E3B]">{t("access_retry")}</button>}
      </div>
      <div>
        <p className="mb-2 text-sm font-semibold text-slate-700">{t("start_city")}</p>
        <select data-testid="ai-planner-city-select" value={city} onChange={(e) => setCity(e.target.value)} className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 font-semibold outline-none">
          {["Padang", "Bukittinggi", "Bandara Internasional Minangkabau (BIM)", "Payakumbuh"].map((c) => <option key={c}>{c}</option>)}
        </select>
      </div>
      <div>
        <p className="mb-2 text-sm font-semibold text-slate-700">{t("interests")}</p>
        <div className="flex flex-wrap gap-2">
          {INTERESTS.map((k) => <button type="button" key={k} data-testid={`ai-planner-interest-checkbox-${k}`} onClick={() => toggle(k)} className={`rounded-full border px-3.5 py-2 text-sm font-semibold transition-colors ${interests.includes(k) ? "border-[#E11D48] bg-rose-50 text-[#E11D48]" : "border-slate-200 text-slate-600 hover:border-slate-400"}`}>{t(`int_${k}`)}</button>)}
        </div>
      </div>
      <button disabled={busy || !ratesReady} data-testid="ai-planner-submit-button" className="flex w-full items-center justify-center gap-2 rounded-full bg-[#E11D48] py-4 font-bold text-white transition-colors hover:bg-[#BE123C] disabled:opacity-60">
        {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Sparkles className="h-5 w-5" />}{busy ? t("generating") : t("generate")}
      </button>
    </form>
  );
}

function Result({ doc }) {
  const { t, money } = useLang();
  const num = useWaNumber();
  const [day, setDay] = useState("all");
  const plan = doc.plan;
  const pts = plan.days.filter((d) => day === "all" || String(d.day) === day).flatMap((d) => d.items.map((it) => ({ name: it.place, subtitle: `${t("day")} ${d.day} · ${it.time}`, lat: it.lat, lng: it.lng })));
  const waText = t("wa_itin") + "\n\n" +
    `${plan.title}\n${doc.input.days} ${t("days")} · ${doc.input.participants} pax · ${money(doc.input.budget)}\n\n` +
    plan.days.map((d) => `${t("day")} ${d.day}: ${d.title}`).join("\n") + `\n\n${t("rec_car")}: ${plan.recommended_car || "-"}`;
  return (
    <div data-testid="ai-itinerary-result" className="space-y-6 fade-up">
      <div className="rounded-3xl bg-[#064E3B] p-6 text-white grain relative overflow-hidden">
        <h2 data-testid="ai-itinerary-title" className="text-2xl sm:text-3xl font-bold">{plan.title}</h2>
        <p className="mt-2 text-emerald-50/80">{plan.summary}</p>
        <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-3">
          <div className="rounded-2xl bg-white/10 p-3"><p className="text-xs text-emerald-100">{t("total_est")}</p><p className="font-extrabold">{money(plan.total_estimate_idr)}</p></div>
          <div className="rounded-2xl bg-white/10 p-3"><p className="text-xs text-emerald-100">{t("per_pax")}</p><p className="font-extrabold">{money(plan.per_person_idr)}</p></div>
          <div className="col-span-2 sm:col-span-1 rounded-2xl bg-white/10 p-3"><p className="text-xs text-emerald-100 flex items-center gap-1"><Car className="h-3 w-3" />{t("rec_car")}</p><p className="font-bold text-sm">{plan.recommended_car}</p></div>
        </div>
        <a href={num ? waLink(num, waText) : "#"} target="_blank" rel="noreferrer" data-testid="ai-itinerary-whatsapp-btn" className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#25D366] px-6 py-3 font-bold text-white hover:brightness-95"><MessageCircle className="h-5 w-5" />{t("book_itin_wa")}</a>
      </div>
      <div>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2"><h3 className="text-xl font-bold">{t("route_map")}</h3>
          <Tabs value={day} onValueChange={setDay}><TabsList className="rounded-full">
            <TabsTrigger value="all" data-testid="ai-day-tab-all" className="rounded-full">{t("cat_all")}</TabsTrigger>
            {plan.days.map((d) => <TabsTrigger key={d.day} value={String(d.day)} data-testid={`ai-day-tab-${d.day}`} className="rounded-full">{d.day}</TabsTrigger>)}
          </TabsList></Tabs>
        </div>
        <MapView points={pts} route height="h-[360px]" />
      </div>
      <div className="space-y-6">
        {plan.days.map((d) => (
          <div key={d.day} data-testid={`ai-day-${d.day}`} className="rounded-3xl border border-slate-200 bg-white p-6">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D97706]">{t("day")} {d.day} · {d.area}</p>
            <h4 className="mt-1 text-lg font-bold">{d.title}</h4>
            <ol className="mt-4 space-y-4 border-l-2 border-dashed border-emerald-200 pl-5">
              {d.items.map((it, i) => (
                <li key={i} className="relative">
                  <span className="absolute -left-[27px] top-1 h-3 w-3 rounded-full bg-[#064E3B] ring-4 ring-emerald-50" />
                  <div className="flex flex-wrap items-baseline justify-between gap-2"><p className="font-semibold"><span className="mr-2 font-mono text-sm text-[#E11D48]">{it.time}</span>{it.activity}</p>{it.cost_idr > 0 && <span className="text-sm font-semibold text-slate-500">{money(it.cost_idr)}</span>}</div>
                  <p className="flex items-center gap-1 text-sm text-slate-500"><MapPin className="h-3.5 w-3.5" />{it.place}</p>
                  {it.tip && <p className="mt-1 text-sm text-slate-600 italic">{it.tip}</p>}
                </li>
              ))}
            </ol>
          </div>
        ))}
      </div>
      <div className="grid gap-6 md:grid-cols-2">
        <div className="rounded-3xl border border-slate-200 bg-white p-6"><h4 className="flex items-center gap-2 font-bold"><Wallet className="h-5 w-5 text-[#064E3B]" />{t("budget_breakdown")}</h4>
          <ul className="mt-3 divide-y">{(plan.budget_breakdown || []).map((b) => <li key={b.label} className="flex justify-between py-2 text-sm"><span>{b.label}</span><b>{money(b.amount_idr)}</b></li>)}</ul></div>
        <div className="rounded-3xl border border-slate-200 bg-white p-6"><h4 className="flex items-center gap-2 font-bold"><Lightbulb className="h-5 w-5 text-[#D97706]" />{t("tips")}</h4>
          <ul className="mt-3 space-y-2 text-sm text-slate-700 list-disc pl-5">{(plan.tips || []).map((x) => <li key={x}>{x}</li>)}</ul></div>
      </div>
    </div>
  );
}

export default function Planner() {
  const { t } = useLang();
  const [doc, setDoc] = useState(null);
  const [busy, setBusy] = useState(false);
  return (
    <div data-testid="planner-page" className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-12">
      <p className="inline-flex items-center gap-2 rounded-full bg-rose-50 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-[#E11D48]"><Sparkles className="h-4 w-4" />Powered by Claude AI</p>
      <h1 className="mt-4 text-4xl sm:text-5xl font-extrabold tracking-tight">{t("planner_title")}</h1>
      <p className="mt-3 max-w-2xl text-base md:text-lg text-slate-500">{t("planner_sub")}</p>
      <div className="mt-10 grid gap-8 lg:grid-cols-12">
        <div className="lg:col-span-5"><div className="lg:sticky lg:top-24"><PlannerForm onResult={setDoc} busy={busy} setBusy={setBusy} /></div></div>
        <div className="lg:col-span-7">
          {busy ? (
            <div data-testid="ai-loading" className="space-y-4 rounded-3xl border border-slate-200 bg-white p-8">
              <Loader2 className="h-8 w-8 animate-spin text-[#E11D48]" /><p className="font-bold">{t("generating")}</p><p className="text-sm text-slate-500">{t("generating_sub")}</p>
              {[1, 2, 3].map((i) => <div key={i} className="h-20 animate-pulse rounded-2xl bg-slate-100" />)}
            </div>
          ) : doc ? <Result doc={doc} /> : (
            <div data-testid="ai-empty" className="flex h-full min-h-[400px] flex-col items-center justify-center rounded-3xl border-2 border-dashed border-slate-200 p-10 text-center">
              <Sparkles className="h-12 w-12 text-amber-400" /><p className="mt-4 text-lg font-bold">{t("empty_plan")}</p><p className="mt-1 max-w-sm text-slate-500">{t("empty_plan_sub")}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
